import {
	ChangeDetectionStrategy,
	Component,
	input,
	InputSignal,
	OnChanges,
	OnDestroy,
	OnInit,
	signal,
	SimpleChanges,
	WritableSignal,
	inject,
} from '@angular/core'
import { OrderClassEnum } from '@modules/orders-module/enums/order-class.enum'
import { OrderSideEnum } from '@modules/orders-module/enums/order-side.enum'
import { OrderTypeEnum } from '@modules/orders-module/enums/order-type.enum'
import { BehaviorSubject, Subject, takeUntil } from 'rxjs'
import { OrdersResponseInterface } from '@modules/orders-module/interfaces/order-response.interface'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { OrdersSettingsInterface } from '@shared/components/trading/widgets/orders-widget/core/interfaces/orders-settings.interface'
import { ColumnInterface } from '@shared/interfaces/column.interface'
import { ColumnsOpenedOrdersEnum } from '@shared/components/trading/widgets/orders-widget/core/enums/stocks/columns-opened-orders.enum'
import { OrderStateEnum } from '@modules/orders-module/enums/order-state.enum'
import { PaginationComponent } from '@shared/components/common/pagination/pagination.component'
import { TradingLauncherService } from '@shared/services/trading-launcher.service'
import { OrderParamsInterface } from '@modules/orders-module/interfaces/order-params.interface'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { NgTemplateOutlet } from '@angular/common'
import { AssetClassEnum } from '@modules/positions-module/enums/asset-class.enum'
import { TitleOrderTypePipe } from '@modules/orders-module/pipes/title-order-type.pipe'
import { TranslatePipe } from '@ngx-translate/core'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { PricePipe } from '@shared/pipes/price.pipe'
import { PercentPipe } from '@shared/pipes/percent.pipe'
import { TitleOrderTermPipe } from '@modules/orders-module/pipes/title-order-term.pipe'
import { TuiBadge, TuiSkeleton } from '@taiga-ui/kit'
import { OrderStatusEnum } from '@modules/orders-module/enums/order-status.enum'
import { OpenedOrdersActionsComponent } from '@shared/components/trading/orders/orders-stocks/tables/opened-orders/common/opened-orders-actions/opened-orders-actions.component'
import { TableColumnComponent } from '@shared/components/common/table/core/components/table-column/table-column.component'
import { TableFooterComponent } from '@shared/components/common/table/core/components/table-footer/table-footer.component'
import { TableActionsComponent } from '@shared/components/common/table/core/components/table-actions/table-actions.component'
import { LiveTickerService } from '@modules/stocks-module/live-ticker/services/live-ticker.service'
import { StocksOrdersService } from '@modules/stocks-module/orders/services/stocks-orders.service'
import { StocksOrdersHelperService } from '@modules/stocks-module/orders/services/stocks-orders-helper.service'
import { WidgetsService } from '@modules/widgets-module/services/widgets.service'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'
import { SecretDirective } from '@shared/directives/secret.directive'
import { TableComponent } from '@shared/components/common/table/table.component'

@UseLocales({ ru, en })
@Component({
	selector: 'app-opened-orders',
	templateUrl: './opened-orders.component.html',
	styleUrl: './opened-orders.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		PaginationComponent,
		NgTemplateOutlet,
		TitleOrderTypePipe,
		TranslatePipe,
		QuantityPipe,
		HighlightDirective,
		PricePipe,
		PercentPipe,
		TitleOrderTermPipe,
		TuiBadge,
		OpenedOrdersActionsComponent,
		TableColumnComponent,
		TableFooterComponent,
		TableActionsComponent,
		SecretDirective,
		TuiSkeleton,
		TableComponent,
	],
})
export class OpenedOrdersComponent implements OnChanges, OnInit, OnDestroy {
	protected readonly orderSideEnum: typeof OrderSideEnum = OrderSideEnum
	protected readonly orderTypeEnum: typeof OrderTypeEnum = OrderTypeEnum
	protected readonly orderClassEnum: typeof OrderClassEnum = OrderClassEnum
	protected readonly orderStateEnum: typeof OrderStateEnum = OrderStateEnum
	protected readonly orderStatusEnum: typeof OrderStatusEnum = OrderStatusEnum
	protected readonly assetClassEnum: typeof AssetClassEnum = AssetClassEnum
	protected readonly columnsOpenedOrdersEnum: typeof ColumnsOpenedOrdersEnum = ColumnsOpenedOrdersEnum

	public widget: InputSignal<WidgetInterface<OrdersSettingsInterface> | null> =
		input<WidgetInterface<OrdersSettingsInterface> | null>(null)

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _ordersStocksService: StocksOrdersService = inject<StocksOrdersService>(StocksOrdersService)
	private readonly _ordersStocksHelperService: StocksOrdersHelperService =
		inject<StocksOrdersHelperService>(StocksOrdersHelperService)
	private readonly _tradingLauncherService: TradingLauncherService =
		inject<TradingLauncherService>(TradingLauncherService)
	private readonly _liveTickerService: LiveTickerService = inject<LiveTickerService>(LiveTickerService)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)
	private readonly _widgetsService: WidgetsService = inject<WidgetsService>(WidgetsService)

	protected ordersResponse: WritableSignal<OrdersResponseInterface | null | undefined> = signal(null)
	protected isLoadingOrders: WritableSignal<boolean> = signal(false)
	protected ordersParams: WritableSignal<OrderParamsInterface | undefined> = signal(undefined)

	protected statesColumns: WritableSignal<ColumnInterface<ColumnsOpenedOrdersEnum>[]> = signal<
		ColumnInterface<ColumnsOpenedOrdersEnum>[]
	>([])
	protected needScrollToTop: WritableSignal<boolean> = signal(false)
	protected isSecret: WritableSignal<boolean> = signal<boolean>(false)
	private _listenSecret(): void {
		this._userHelperService.isSecret$.pipe(takeUntil(this._destroy$)).subscribe((value: boolean): void => {
			this.isSecret.set(value)
		})
	}

	private _updateStatesColumns(): void {
		const widget: WidgetInterface<OrdersSettingsInterface> | null = this.widget()
		if (!widget) return

		this.statesColumns.set([...widget.settings.stocks.open.columns])
	}

	private _launchOpenedOrders(): void {
		this._tradingLauncherService
			.launchOrders$(this.orderStateEnum.OPEN)
			.pipe(takeUntil(this._destroy$))
			.subscribe(
				([ordersResponse, isLoading, orderRequest, isNeedUpdate]: [
					OrdersResponseInterface | null | undefined,
					boolean,
					OrderParamsInterface | undefined,
					boolean,
				]): void => {
					if (this.isLoadingOrders() !== isLoading) this.needScrollToTop.set(true)

					this.ordersResponse.set(ordersResponse)
					this.isLoadingOrders.set(isLoading)
					this.ordersParams.set(orderRequest)

					setTimeout((): void => this.needScrollToTop.set(false))
					if (isNeedUpdate) {
						this._ordersStocksHelperService.isNeedUpdateOrderResponseMap
							.get(this.orderStateEnum.OPEN)
							?.next(false)
						this._getOpenedOrders()
					}
				},
			)
	}

	private _getOpenedOrders(): void {
		this._ordersStocksService.getAllOrders(this.orderStateEnum.OPEN).pipe(takeUntil(this._destroy$)).subscribe()
	}

	protected onPageChange(newPage: number): void {
		const ordersParams$: BehaviorSubject<OrderParamsInterface> | undefined =
			this._ordersStocksHelperService.ordersParamsMap.get(this.orderStateEnum.OPEN)
		if (!ordersParams$) return

		ordersParams$.next({ ...ordersParams$.getValue(), page: newPage })

		this._getOpenedOrders()
		this.isLoadingOrders.set(true)
	}

	protected setLiveTicker(symbol: string): void {
		this._widgetsService.setLiveTickerToDashboard(symbol)
		this._liveTickerService.launchLiveTicker(symbol)
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['widget']) this._updateStatesColumns()
	}

	ngOnInit(): void {
		this._listenSecret()
		this._launchOpenedOrders()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
